import client from '../client';
import type { ApiResponse } from '../../types/api';

// Anthropic-format content blocks (the backend returns them raw from Claude)
export type ChatTextBlock = { type: 'text'; text: string };
export type ChatToolUseBlock = { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> };
export type ChatToolResultBlock = {
  type: 'tool_result';
  tool_use_id: string;
  content: string;
  is_error?: boolean;
};
// Extended-thinking blocks. The model can return these and they MUST be
// preserved verbatim (with signature) when replaying the assistant turn for
// a follow-up tool_result, or the API rejects with 400.
export type ChatThinkingBlock = { type: 'thinking'; thinking: string; signature: string };
export type ChatContentBlock =
  | ChatTextBlock
  | ChatToolUseBlock
  | ChatToolResultBlock
  | ChatThinkingBlock;

export type ChatMessage =
  | { role: 'user'; content: string | ChatContentBlock[] }
  | { role: 'assistant'; content: string | ChatContentBlock[] };

export interface ChatToolSchema {
  name: string;
  description: string;
  input_schema: {
    type: 'object';
    properties: Record<string, unknown>;
    required?: string[];
  };
}

export interface ChatResponse {
  content: ChatContentBlock[];
  stop_reason: string | null;
  usage: { input_tokens?: number; output_tokens?: number } | null;
}

export interface ChatStreamEvents {
  onText?: (delta: string) => void;
  onToolUse?: (block: ChatToolUseBlock) => void;
  onDone?: (info: { stop_reason: string | null }) => void;
  onError?: (message: string) => void;
}

export const aiApi = {
  write: async (
    storeId: string,
    data: { prompt: string; currentContent?: string; productTitle?: string; contentType?: string },
  ) => {
    const res = await client.post<ApiResponse<{ content: string }>>(
      `/dashboard/${storeId}/ai/write`,
      data,
    );
    return res.data.data!;
  },

  chat: async (
    storeId: string,
    body: {
      messages: ChatMessage[];
      tools: ChatToolSchema[];
      systemPrompt?: string;
      // Persisted on the backend so admin can group multi-turn conversations.
      conversationId?: string;
      turnNumber?: number;
    },
  ) => {
    const res = await client.post<ApiResponse<ChatResponse>>(
      `/dashboard/${storeId}/ai/chat`,
      body,
    );
    return res.data.data!;
  },

  /**
   * Streaming chat. Opens an SSE connection to /ai/chat-stream and dispatches
   * events as they arrive. Resolves once the stream finishes (or errors).
   * Returns the assembled assistant content blocks so the caller can append
   * them to the conversation history for the next turn.
   */
  chatStream: async (
    storeId: string,
    body: {
      messages: ChatMessage[];
      tools: ChatToolSchema[];
      systemPrompt?: string;
      conversationId?: string;
      turnNumber?: number;
    },
    events: ChatStreamEvents = {},
    signal?: AbortSignal,
  ): Promise<{ content: ChatContentBlock[]; stop_reason: string | null }> => {
    const response = await fetch(`/api/v1/dashboard/${storeId}/ai/chat-stream`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok || !response.body) {
      let detail = '';
      try {
        const json = await response.json();
        detail = json?.message || json?.error || '';
      } catch (e) {
        try { detail = await response.text(); } catch (e2) { /* ignore */ }
      }
      throw new Error(detail || `Streaming request failed (${response.status})`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const collected: ChatContentBlock[] = [];
    let currentText = '';
    let stopReason: string | null = null;

    const flushTextBlock = () => {
      if (currentText) {
        collected.push({ type: 'text', text: currentText });
        currentText = '';
      }
    };

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line (\n\n).
        let sepIdx;
        while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
          if (!dataLine) continue;
          const json = dataLine.slice(6);
          if (!json) continue;
          let parsed: any;
          try {
            parsed = JSON.parse(json);
          } catch (e) {
            continue;
          }
          if (parsed.type === 'text') {
            currentText += parsed.delta;
            events.onText?.(parsed.delta);
          } else if (parsed.type === 'thinking') {
            flushTextBlock();
            const block: ChatThinkingBlock = {
              type: 'thinking',
              thinking: parsed.thinking || '',
              signature: parsed.signature || '',
            };
            collected.push(block);
          } else if (parsed.type === 'tool_use') {
            flushTextBlock();
            const block: ChatToolUseBlock = {
              type: 'tool_use',
              id: parsed.id,
              name: parsed.name,
              input: parsed.input || {},
            };
            collected.push(block);
            events.onToolUse?.(block);
          } else if (parsed.type === 'done') {
            flushTextBlock();
            stopReason = parsed.stop_reason ?? null;
            events.onDone?.({ stop_reason: stopReason });
          } else if (parsed.type === 'error') {
            flushTextBlock();
            const msg = parsed.message || 'streaming error';
            events.onError?.(msg);
            throw new Error(msg);
          }
        }
      }
    } finally {
      try { reader.releaseLock(); } catch (e) { /* already released */ }
    }

    flushTextBlock();
    return { content: collected, stop_reason: stopReason };
  },
};
